import { AssetHubToken, EvmToken, FiatToken, OnChainToken } from "../tokens";
import { isOnChainToken } from "../tokens/utils/typeGuards";
import { RampDirection } from "../types";

// GET /prices?provider=:provider&sourceCurrency=:sourceCurrency&targetCurrency=:targetCurrency&amount=:amount&network=:network&direction=:direction
export const VALID_PROVIDERS = ["alchemypay", "moonpay", "transak", "vortex"] as const;

export const VALID_CRYPTO_CURRENCIES = [...Object.values(EvmToken), ...Object.values(AssetHubToken)] as const;

export const VALID_FIAT_CURRENCIES = Object.values(FiatToken);

export type PriceProvider = (typeof VALID_PROVIDERS)[number];
export type CryptoCurrency = OnChainToken;
export type FiatCurrency = (typeof VALID_FIAT_CURRENCIES)[number];
export type Currency = CryptoCurrency | FiatCurrency;

// Validation functions
export const isValidPriceProvider = (value: unknown): value is PriceProvider =>
  typeof value === "string" && VALID_PROVIDERS.includes(value.toLowerCase() as PriceProvider);

export const isValidCryptoCurrency = (value: unknown): value is CryptoCurrency => {
  if (typeof value !== "string") return false;
  return isOnChainToken(value);
};

export const isValidFiatCurrency = (value: unknown): value is FiatCurrency => {
  if (typeof value !== "string") return false;

  const normalizedValue = value.toUpperCase();
  return VALID_FIAT_CURRENCIES.includes(normalizedValue as FiatCurrency);
};

export interface PriceRequest {
  provider: PriceProvider;
  sourceCurrency: Currency;
  targetCurrency: Currency;
  amount: string;
  network?: string;
  direction: RampDirection;
}

// Validation function for direction
export const isValidDirection = (value: unknown): value is RampDirection =>
  typeof value === "string" && Object.values(RampDirection).includes(value as RampDirection);

// Validation function for currency based on direction
export const isValidCurrencyForDirection = (currency: unknown, expectedType: "crypto" | "fiat"): boolean => {
  return expectedType === "crypto" ? isValidCryptoCurrency(currency) : isValidFiatCurrency(currency);
};

/**
 * The standardized response format for all price providers.
 *
 * Direction-specific interpretation guide:
 *
 * For onramp (buying crypto with fiat):
 * - requestedAmount: The fiat amount the user is spending
 * - quoteAmount: The crypto amount the user will receive
 * - conversionPrice: The price of 1 unit of crypto in fiat
 *
 * For offramp (selling crypto for fiat):
 * - requestedAmount: The crypto amount the user is selling
 * - quoteAmount: The fiat amount the user will receive
 * - conversionPrice: The price of 1 unit of crypto in fiat
 */
export interface PriceResponseBase {
  requestedAmount: number;
  quoteAmount: number;
  totalFee: number;
  direction: RampDirection;
}

/**
 * Provider-specific response types
 * Each provider extends the base response with its own identifier
 */
export interface AlchemyPayPriceResponse extends PriceResponseBase {
  provider: "alchemypay";
  // AlchemyPay specific fields can be added here
}

export interface MoonpayPriceResponse extends PriceResponseBase {
  provider: "moonpay";
  // Moonpay specific fields can be added here
}

export interface TransakPriceResponse extends PriceResponseBase {
  provider: "transak";
  // Transak specific fields can be added here
}

export interface VortexPriceResponse extends PriceResponseBase {
  provider: "vortex";
  // Vortex specific fields can be added here
}

export type PriceResponse = AlchemyPayPriceResponse | MoonpayPriceResponse | TransakPriceResponse | VortexPriceResponse;

export interface PriceErrorResponse {
  error: string;
}

// Types for the bundled price endpoint (GET /prices/all)

// Represents the result for a single provider in the bundled response
export type BundledPriceResult =
  | {
      status: "fulfilled";
      value: PriceResponse;
    }
  | {
      status: "rejected";
      reason: {
        message: string;
        status?: number;
      };
    };

// The complete response from the bundled price endpoint
export type AllPricesResponse = {
  [K in PriceProvider]?: BundledPriceResult;
};
